/**
 * Prep task completion cascade (CLAUDE.md §28.4 with sub-tasks).
 *
 * ONE pure rule, applied to the whole task array and returning a NEW
 * array (never mutating the input):
 *
 *   - ticking a top-level task that HAS sub-tasks ticks all of them;
 *     un-ticking it un-ticks all of them;
 *   - ticking the last un-ticked sub-task of a parent marks the parent
 *     complete; un-ticking any sub-task marks the parent incomplete;
 *   - a top-level task with no sub-tasks simply flips, as before.
 *
 * Parents are resolved with the read-tolerant rule in prepTasks.js, so a
 * legacy row (no `parent` fields) behaves exactly as it always did.
 * Callers persist the result through normalisePrepTasks (the save-path
 * safety net). Nothing here touches statistics.
 */

import { resolveParents } from './prepTasks';

/**
 * Set task `id` to `completed`, cascading per the rule above.
 * Returns a new array; returns the input unchanged if `id` is unknown.
 */
export function setTaskCompletion(tasks, id, completed) {
  const list = resolveParents(tasks);
  const item = list.find((t) => t.id === id);
  if (!item) return Array.isArray(tasks) ? tasks : [];
  const done = completed === true;

  const kids = list.filter((t) => t.parent === id);
  if (kids.length > 0) {
    // Parent with sub-tasks: the parent and every sub-task follow the tick.
    return list.map((t) => (t.id === id || t.parent === id ? { ...t, completed: done } : t));
  }

  if (item.parent) {
    // Sub-task: flip it, then derive the parent from ALL its sub-tasks.
    const next = list.map((t) => (t.id === id ? { ...t, completed: done } : t));
    const siblings = next.filter((t) => t.parent === item.parent);
    const allDone = siblings.length > 0 && siblings.every((t) => t.completed === true);
    return next.map((t) => (t.id === item.parent ? { ...t, completed: allDone } : t));
  }

  // Top-level task without sub-tasks: behaves as it always has.
  return list.map((t) => (t.id === id ? { ...t, completed: done } : t));
}

/** Flip task `id` (cascading). Returns a new array. */
export function toggleTaskCompletion(tasks, id) {
  const list = resolveParents(tasks);
  const item = list.find((t) => t.id === id);
  if (!item) return Array.isArray(tasks) ? tasks : [];
  return setTaskCompletion(list, id, item.completed !== true);
}
